import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * MessageMultiple component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconMessageMultiple extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon cat-c-icon-bi-directional', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.97605 2.60498C4.76102 2.60498 4.55479 2.6904 4.40275 2.84245C4.2507 2.9945 4.16528 3.20072 4.16528 3.41575V12.7804C4.16528 12.861 4.15228 12.9411 4.12679 13.0175L2.99968 16.3989L4.01148 16.1425C4.41301 16.0408 4.82097 16.2839 4.92269 16.6854C5.02442 17.0869 4.78137 17.4949 4.37984 17.5966L2.03869 18.1897C1.77293 18.257 1.49161 18.174 1.30502 17.9731C1.11842 17.7723 1.0563 17.4856 1.143 17.2255L2.66528 12.6587V3.41575C2.66528 2.8029 2.90873 2.21514 3.34209 1.78179C3.77544 1.34844 4.36319 1.10498 4.97605 1.10498H17.4622C18.0751 1.10498 18.6628 1.34844 19.0962 1.78179C19.5295 2.21514 19.773 2.8029 19.773 3.41575V5.7569C19.773 6.17112 19.4372 6.5069 19.023 6.5069C18.6088 6.5069 18.273 6.17112 18.273 5.7569V3.41575C18.273 3.20072 18.1875 2.9945 18.0355 2.84245C17.8835 2.6904 17.6772 2.60498 17.4622 2.60498H4.97605Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.02447 9.58574C8.45782 9.15239 9.04557 8.90894 9.65843 8.90894H19.8034C20.4163 8.90894 21.004 9.15239 21.4374 9.58574C21.8707 10.0191 22.1142 10.6069 22.1142 11.2197V17.4007L22.8844 22.0218C22.9267 22.2755 22.836 22.5332 22.6441 22.7046C22.4522 22.8759 22.1859 22.9369 21.9385 22.8662L16.5768 21.3343H9.65843C9.04557 21.3343 8.45782 21.0909 8.02447 20.6575C7.59111 20.2242 7.34766 19.6364 7.34766 19.0236V11.2197C7.34766 10.6069 7.59111 10.0191 8.02447 9.58574ZM9.65843 10.4089C9.4434 10.4089 9.23717 10.4944 9.08513 10.6464C8.93308 10.7985 8.84766 11.0047 8.84766 11.2197V19.0236C8.84766 19.2386 8.93308 19.4448 9.08513 19.5969C9.23717 19.7489 9.4434 19.8343 9.65843 19.8343H16.6819C16.7516 19.8343 16.8209 19.844 16.8879 19.8632L21.2097 21.098L20.6244 17.5861C20.6176 17.5453 20.6142 17.5041 20.6142 17.4628V11.2197C20.6142 11.0047 20.5288 10.7985 20.3767 10.6464C20.2247 10.4944 20.0185 10.4089 19.8034 10.4089H9.65843Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-message-multiple') === undefined) {
  customElements.define('cat-icon-message-multiple', BlocksIconMessageMultiple);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-message-multiple': BlocksIconMessageMultiple;
  }
}
